import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlIcon } from '@shoelace-style/shoelace/dist/react';
import { Toast } from '../../../shared/Toast';
import { relayCreativeMessage } from '../../../services/creativeChatSocket';
import CreativeList from './tabs/CreativeList';
import FilterBar from './components/FilterBar';
import DetailDrawer from './components/DetailDrawer';
import UploadDesignModal from './components/UploadDesignModal';
import ConfirmDialog from './components/ConfirmDialog';
import useCreativeList from './hooks/useCreativeList';
import {
  createCancelSource,
  fetchCounts,
  isCancel,
  pickCreative,
  sendMessage,
  uploadAttachment,
} from './api';
import {
  DEFAULT_TAB,
  EMPTY_FILTERS,
  STORAGE_KEY,
  TABS,
  TAB_KEYS,
  filterKeyOf,
  getTab,
  hasActiveFilters,
  normaliseFilters,
} from './config';

/**
 * Inline visually-hidden style. We do NOT use Tailwind's `.sr-only` here:
 * `src/Styles.scss:266` redefines `.sr-only` (position/width/height: unset) and
 * is imported after `index.css`, so with equal specificity the broken rule wins
 * and anything marked `sr-only` renders fully visible. An inline style beats a
 * stylesheet rule that carries no `!important`, so this cannot be overridden.
 * Keep the element in the DOM so screen readers still announce it.
 */
const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  borderWidth: 0,
};

/** `{ tab, filters }` from localStorage, defensively parsed. */
const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { tab: null, filters: EMPTY_FILTERS };
    const parsed = JSON.parse(raw);
    return {
      tab: TAB_KEYS.includes(parsed?.tab) ? parsed.tab : null,
      filters: normaliseFilters(parsed?.filters),
    };
  } catch (e) {
    return { tab: null, filters: EMPTY_FILTERS };
  }
};

/**
 * Newest server `created_time` across the logs an action just returned, or
 * `null` when there is nothing usable. The browser clock is never used for
 * this: the very same event is rendered in the thread from the server's
 * timestamp, so a skewed client clock would make the list and the thread
 * disagree about when it happened. `null` means "leave the previous value".
 */
const latestLogTime = (logs) => {
  if (!Array.isArray(logs)) return null;
  let max = null;
  for (let i = 0; i < logs.length; i += 1) {
    const t = Number(logs[i]?.created_time);
    if (!Number.isFinite(t) || t <= 0) continue;
    if (max === null || t > max) max = t;
  }
  return max;
};

/**
 * How many of the returned logs are conversation rows. An attachment is a
 * `message` log carrying a `media_url` (or, for older rows, an `attachment`
 * entry in `changes`), so this counts both plain messages and attachments —
 * which is exactly what the thread renders as a bubble, so the list badge
 * and the thread cannot drift.
 */
const countMessageLogs = (logs) =>
  Array.isArray(logs) ? logs.filter((log) => log?.activity_type === 'message').length : 0;

const OrderBookingHome = () => {
  const storedRef = useRef(readStored());
  const [searchParams, setSearchParams] = useSearchParams();

  // The URL query string is the source of truth for the active tab, so
  // back/forward and a hard refresh all land on the right list.
  const urlTab = searchParams.get('tab');
  const tab = TAB_KEYS.includes(urlTab) ? urlTab : storedRef.current.tab || DEFAULT_TAB;

  const [filters, setFilters] = useState(storedRef.current.filters);
  const filterKey = useMemo(() => filterKeyOf(filters), [filters]);

  const [counts, setCounts] = useState(null);
  const [countsError, setCountsError] = useState(null);

  const [selected, setSelected] = useState(null);
  const [uploadRow, setUploadRow] = useState(null);
  const [pickingId, setPickingId] = useState(null);
  const [pickConfirmRow, setPickConfirmRow] = useState(null);
  const [incoming, setIncoming] = useState(null);

  const mountedRef = useRef(true);
  const nonceRef = useRef(0);
  const countsReqRef = useRef(0);
  const countsSourceRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (countsSourceRef.current) countsSourceRef.current.cancel('component unmounted');
    };
  }, []);

  const { rows, total, hasMore, loading, loadingMore, error, reload, loadMore, patchRow, removeRow } =
    useCreativeList({ tab, filterKey });

  /* ----------------------------- persistence ---------------------------- */

  // Normalise the URL once so a bare /order-booking still shows ?tab=…
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  useEffect(() => {
    if (urlTab === tab) return;
    setSearchParamsRef.current((prev) => { const n = new URLSearchParams(prev); n.set('tab', tab); return n; }, { replace: true });
  }, [urlTab, tab]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ tab, filters: JSON.parse(filterKey) }));
    } catch (e) {
      /* storage full or disabled — the screen still works */
    }
  }, [tab, filterKey]);

  /* -------------------------------- counts ------------------------------ */

  const loadCounts = useCallback(async () => {
    const reqId = countsReqRef.current + 1;
    countsReqRef.current = reqId;
    if (countsSourceRef.current) countsSourceRef.current.cancel('superseded');
    const source = createCancelSource();
    countsSourceRef.current = source;

    let parsed = {};
    try {
      parsed = JSON.parse(filterKey);
    } catch (e) {
      parsed = {};
    }

    try {
      const data = await fetchCounts(parsed, source.token);
      if (!mountedRef.current || reqId !== countsReqRef.current) return;
      setCounts(data || null);
      setCountsError(null);
    } catch (err) {
      if (isCancel(err)) return;
      if (!mountedRef.current || reqId !== countsReqRef.current) return;
      setCountsError(err?.message || 'Unable to load the tab counts.');
    }
  }, [filterKey]);

  useEffect(() => {
    loadCounts();
    return () => {
      if (countsSourceRef.current) countsSourceRef.current.cancel('filters changed');
    };
  }, [loadCounts]);

  /* ------------------------------- handlers ----------------------------- */

  const changeTab = useCallback(
    (nextTab) => {
      if (nextTab === tab) return;
      setSelected(null);
      setUploadRow(null);
      setSearchParamsRef.current((prev) => { const n = new URLSearchParams(prev); n.set('tab', nextTab); return n; });
    },
    [tab]
  );

  const updateFilters = useCallback((patch) => {
    setFilters((prev) => normaliseFilters({ ...prev, ...patch }));
  }, []);

  const clearFilters = useCallback(() => setFilters(EMPTY_FILTERS), []);

  const pushIncoming = useCallback((payload) => {
    nonceRef.current += 1;
    setIncoming({ ...payload, nonce: nonceRef.current });
  }, []);

  /** Keep the row in the tab it still belongs to; otherwise drop it. */
  const reconcileRow = useCallback(
    (bscId, patch) => {
      const nextStatus = patch?.status;
      if (nextStatus != null && Number(nextStatus) !== getTab(tab).status) removeRow(bscId);
      else patchRow(bscId, patch);
    },
    [tab, patchRow, removeRow]
  );

  /**
   * Fold what the server actually reported into the list row. Both values are
   * derived from the returned logs — nothing here is guessed — and each is
   * skipped when the response carried nothing for it, so a previous real
   * value is left alone rather than being overwritten with an invention.
   */
  const applyLogsToRow = useCallback(
    (bscId, logs) => {
      const at = latestLogTime(logs);
      const messages = countMessageLogs(logs);
      if (at == null && messages === 0) return;
      patchRow(bscId, (row) => {
        const patch = {};
        if (at != null) patch.last_activity_time = at;
        if (messages > 0) patch.message_count = Number(row.message_count || 0) + messages;
        return patch;
      });
    },
    [patchRow]
  );

  const handlePick = useCallback(
    async (row) => {
      if (!row || pickingId != null) return;
      setPickingId(row.bsc_id);
      try {
        const data = await pickCreative(row.bsc_id);
        if (!mountedRef.current) return;
        const nextStatus = data?.status ?? 3;
        pushIncoming({
          bsc_id: row.bsc_id,
          logs: data?.logs || [],
          patch: { status: nextStatus },
        });
        reconcileRow(row.bsc_id, { status: nextStatus });
        setSelected((prev) =>
          prev && Number(prev.bsc_id) === Number(row.bsc_id)
            ? { ...prev, status: nextStatus }
            : prev
        );
        loadCounts();
        Toast('success', 'Picked — you will find it under In Progress');
      } catch (err) {
        Toast('error', err?.message || 'Unable to pick this creative.');
      } finally {
        if (mountedRef.current) setPickingId(null);
      }
    },
    [pickingId, pushIncoming, reconcileRow, loadCounts]
  );

  // Both entry points (the list buttons and the drawer action bar) route
  // through here, so the confirmation lives in exactly one place.
  const requestPick = useCallback(
    (row) => {
      if (!row || pickingId != null) return;
      setPickConfirmRow(row);
    },
    [pickingId]
  );

  const confirmPick = useCallback(async () => {
    const row = pickConfirmRow;
    if (!row) return;
    await handlePick(row);
    if (mountedRef.current) setPickConfirmRow(null);
  }, [pickConfirmRow, handlePick]);

  const handleSendMessage = useCallback(
    async (bscId, text) => {
      try {
        const data = await sendMessage(bscId, text);
        if (!mountedRef.current) return false;
        const logs = data?.logs || [];
        pushIncoming({ bsc_id: bscId, logs });
        applyLogsToRow(bscId, logs);
        logs.forEach((log) => relayCreativeMessage(bscId, log));
        return true;
      } catch (err) {
        Toast('error', err?.message || 'Unable to send your message.');
        return false;
      }
    },
    [pushIncoming, applyLogsToRow]
  );

  const handleSendAttachment = useCallback(
    async (bscId, file, caption, onUploadProgress) => {
      try {
        const data = await uploadAttachment({
          bsc_id: bscId,
          file,
          message: caption,
          onUploadProgress,
        });
        if (!mountedRef.current) return false;
        const logs = data?.logs || [];
        pushIncoming({ bsc_id: bscId, logs });
        applyLogsToRow(bscId, logs);
        logs.forEach((log) => relayCreativeMessage(bscId, log));
        Toast('success', 'Attachment shared');
        return true;
      } catch (err) {
        Toast('error', err?.message || 'Unable to share that file.');
        return false;
      }
    },
    [pushIncoming, applyLogsToRow]
  );

  const handleUploaded = useCallback(
    ({ bsc_id: bscId, status, logs, imgUrl, videoUrl }) => {
      const patch = {};
      if (imgUrl) patch.img_urls = imgUrl;
      if (videoUrl) patch.video_urls = videoUrl;
      if (status != null) patch.status = status;
      const at = latestLogTime(logs);
      if (at != null) patch.last_activity_time = at;

      pushIncoming({ bsc_id: bscId, logs: logs || [], patch, invalidateVersions: true });
      reconcileRow(bscId, patch);
      setSelected((prev) =>
        prev && Number(prev.bsc_id) === Number(bscId) ? { ...prev, ...patch } : prev
      );
      loadCounts();
    },
    [pushIncoming, reconcileRow, loadCounts]
  );

  const handleRefresh = useCallback(() => {
    reload();
    loadCounts();
  }, [reload, loadCounts]);

  const filtered = hasActiveFilters(filters);
  const activeTab = getTab(tab);

  return (
    <div className="pb-8 pt-4">
      {/* Sticky page head: title, tabs and filters stay reachable while scrolling. */}
      <div className="sticky top-0 z-30 border-b border-slate-200 bg-white pb-3 pt-1">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
              <SlIcon
                library="lucide"
                name="package"
                className="text-sky-600"
                style={{ fontSize: '20px' }}
              />
              Order Booking Creatives
            </h1>
            <p className="mt-0.5 text-sm text-slate-500">
              Designs booked through order booking and assigned to you.
            </p>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
          >
            <SlIcon
              library="lucide"
              name="refresh-cw"
              className={loading ? 'animate-spin' : ''}
              style={{ fontSize: '14px' }}
            />
            Refresh
          </button>
        </div>

        <div
          role="tablist"
          aria-label="Creative status"
          className="mt-3 flex gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1"
        >
          {TABS.map((t) => {
            const isActive = t.key === tab;
            const count = counts ? counts[t.key] : null;
            return (
              <button
                key={t.key}
                role="tab"
                type="button"
                aria-selected={isActive}
                onClick={() => changeTab(t.key)}
                className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition ${
                  isActive
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {t.label}
                <span
                  className={`inline-flex min-w-[20px] justify-center rounded-full px-1.5 py-[1px] text-[11px] font-semibold ${
                    isActive ? 'bg-sky-100 text-sky-700' : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {count == null ? '–' : count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-3">
          <FilterBar filters={filters} onChange={updateFilters} onClear={clearFilters} />
        </div>
      </div>

      {countsError && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          <span>{countsError}</span>
          <button
            type="button"
            onClick={loadCounts}
            className="rounded-lg border border-amber-300 bg-white px-3 py-1 text-xs font-semibold text-amber-800 transition hover:bg-amber-100"
          >
            Retry counts
          </button>
        </div>
      )}

      <div className="mt-4">
        <CreativeList
          tab={tab}
          rows={rows}
          total={total}
          hasMore={hasMore}
          loading={loading}
          loadingMore={loadingMore}
          error={error}
          onRetry={reload}
          onLoadMore={loadMore}
          onOpen={setSelected}
          onPick={requestPick}
          onUpload={setUploadRow}
          busyId={pickingId}
          filtered={filtered}
          onClearFilters={clearFilters}
        />
      </div>

      <DetailDrawer
        open={!!selected}
        row={selected}
        onClose={() => setSelected(null)}
        onPick={requestPick}
        onOpenUpload={setUploadRow}
        onSendMessage={handleSendMessage}
        onSendAttachment={handleSendAttachment}
        incoming={incoming}
        pickBusy={selected ? Number(pickingId) === Number(selected.bsc_id) : false}
      />

      <UploadDesignModal
        open={!!uploadRow}
        row={uploadRow}
        onClose={() => setUploadRow(null)}
        onUploaded={handleUploaded}
      />

      <ConfirmDialog
        open={!!pickConfirmRow}
        title="Pick this creative?"
        message={
          pickConfirmRow
            ? `“${
                pickConfirmRow.creative_name || `Creative #${pickConfirmRow.bsc_id}`
              }” moves to In Progress and is assigned to you to complete. Sales will see that you have started on it.`
            : ''
        }
        confirmLabel="Pick & start"
        cancelLabel="Not now"
        tone="primary"
        busy={pickingId != null}
        onCancel={() => setPickConfirmRow(null)}
        onConfirm={confirmPick}
      />

      {/* Screen-reader announcer. Inline VISUALLY_HIDDEN, not `sr-only` — see
          Styles.scss:266, which would otherwise render this text on the page. */}
      <span style={VISUALLY_HIDDEN} aria-live="polite">
        {`${activeTab.label}: ${rows.length} of ${total} creatives loaded`}
      </span>
    </div>
  );
};

export default OrderBookingHome;
